import {HttpClient, HttpResponse, MessageError, RequestOptions, resolveResponseError} from "@firefly/fly-http";
import {onAccessDeniedHandler, onInsufficientAuthenticationException} from "./handler";
import {ClientOptions} from "./typing";
import {GraphQLErrors, SecurityErrors} from "./const";

export type OperationVariables = Record<string, any>;
export interface QueryOptions<TVariables = OperationVariables, _TData = any> {
    query: any;
    variables: TVariables;

}
export interface MutationOptions<TVariables = OperationVariables, _TData = any> {
    mutation: any;
    variables: TVariables;
}

export class GraphQLException extends Error {
    constructor(message: string, _detail?: any) {
        super(message);
        this.name = "GraphQLException";
    }
}

export function isGraphQLException(error: any): error is GraphQLException {
    return error.name === 'GraphQLException';
}

type GraphqlResponseType<T = any> = {
    data: T,
    errors: any,
}

// export type GrRequestOptions = {
//     headers?: {
//         [name: string]: string | string[];
//     };
// }
function request<T = any>(client: HttpClient, target: string, graphql: {
    query?: any;
    variables: any;
    extensions: Record<string, string>,
}, options: RequestOptions): Promise<GraphqlResponseType<T>> {
    const {
        headers = {}
    } = options;

    return new Promise<GraphqlResponseType<T>>((resolve, reject) => {
        client.post(target, graphql, {
            headers: headers,
            observe: 'response',
        }).subscribe({
            next: (response: HttpResponse<any>) => {
                const {
                    body
                } = response;
                if (body.error) {
                    const errorInfo = body.error;
                    const message = errorInfo.message || errorInfo.code;
                    reject(
                        new MessageError(message, errorInfo,{
                            response: response,
                            request: {
                                url: `/graphql`,
                                method: 'POST'
                            },
                        }),
                    );
                }
                return resolve(body);
            },
            error: (response: HttpResponse<any>) => {
                reject(resolveResponseError({
                    url: `/graphql`,
                    method: 'POST'
                }, response));
            }
        });
    })
}


export interface GraphQLClientOptions extends ClientOptions {
    /**
     * @example /graphql
     */
    target: string;
    // client: HttpClient;
    extensions?: Record<string, string>;
}
export class GraphQLClient {
    private readonly extensions: Record<string, string>;
    private target: string;
    private client: HttpClient;
    // private readonly onUnauthorized: () => void;
    constructor(options: GraphQLClientOptions) {
        const {
            target,
            handler,
            extensions = {},
        } = options;
        this.target = target;
        this.client = new HttpClient(handler);
        this.extensions = extensions;
    }

    public wrap(promise: Promise<any>): Promise<any> {
        return promise.then((response) => {
            if (response.errors) {
                const errors = response.errors;
                let resolved: any = false;
                for (let i = 0; i < errors.length; i++) {
                    const error = errors[i];
                    resolved = this.handleException(error)
                    if (resolved) {
                        break;
                    }
                }
                if (resolved) {
                    return resolved;
                } else {
                    const error = response.errors[0];
                    const message = error.message || '请求出错';
                    throw new GraphQLException(message, response.errors)
                }

            }
            return response.data;
        })
    }
    query<T = any, TVariables = OperationVariables>(
        query: QueryOptions<TVariables, T>,
        options: RequestOptions = {},
    ): Promise<T> {
        const execute = () => {
            return request(this.client, this.target, {
                query: query.query,
                variables: query.variables,
                extensions: this.extensions,
            }, options)
        }
        return this.wrap(execute());
    }

    mutate<T = any, TVariables = OperationVariables>(
        mutation: MutationOptions<TVariables, T>,
        options: RequestOptions = {}
    ): Promise<T> {
        const execute = () => {
            return request(this.client, this.target, {
                query: mutation.mutation,
                variables: mutation.variables,
                extensions: this.extensions,
            }, options)
        }
        return this.wrap(execute());
    }

    private handleException(error: any): Promise<void> | void {
        const classification = error.extensions.classification;
        if (classification === SecurityErrors.INSUFFICIENT_AUTHENTICATION) {
            return onInsufficientAuthenticationException(classification);
        }
        if (
            //TODO 准备废弃
            classification === 'UNAUTHORIZED' ||
            classification === 'UNAUTHENTICATED' ||
            classification === GraphQLErrors.AUTHENTICATION) {
            // this.token.invalidate().then();
            return onAccessDeniedHandler(classification);
            // return this.onUnauthorized();
        }
        return;
    }
}

